import React, { useState, useEffect } from "react";
import styles from "./BeverageV3Landing.module.css";
import { BtnUi } from "~/components";
import { useAcid } from "../../hooks/useAcid";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { sendPageViewWhenConsentReady } from "../../utils/adobeDataLayerUtils";

export const BeverageV3ThankYou = () => {
  const { acid } = useAcid();
  const [optIn, setOptIn] = useState(false); // Default to opted-out
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    return sendPageViewWhenConsentReady(
      "Beverage V3 Thank You Page",
      "Passions-FavBevs",
      "beverages3",
    );
  }, []);

  // Clean up sessionStorage when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("savedBeverageV3Preferences");
    };
  }, []);

  const handleDone = () => {
    window.location.href = "https://www.marriott.com/default.mi";
  };

  const handleOptInChange = async (e) => {
    const isOptedIn = e.target.checked;
    setOptIn(isOptedIn);

    // Get the previously saved beverage preferences from sessionStorage
    const savedPreferences = sessionStorage.getItem("savedBeverageV3Preferences");
    const structuredBeverageData = savedPreferences
      ? JSON.parse(savedPreferences)
      : [];

    // Save the personalization preference immediately when changed
    setIsSaving(true);
    try {
      await actionWebInvoke(
        actions["marriott-passions-v3/save-beverage-preferences"],
        {},
        {
          acid,
          structuredBeverageData, // Re-affirm previously saved preferences alongside the updated consent value
          optedOutPersonalization: !isOptedIn, // Invert: opted-in = false for optedOut, opted-out = true for optedOut
        },
      );
      /**
       * Keep the shared consent keys in sync — without this, BeverageV3's
       * own sessionOptedOut state (seeded once at mount from these same
       * keys) stays stale, so the next category the guest saves on
       * /beverage3 would silently resubmit the pre-checkbox consent value.
       */
      sessionStorage.setItem("beverageConsentResponded", "true");
      sessionStorage.setItem("beverageConsentOptedOut", String(!isOptedIn));
    } catch (error) {
      console.error("Error updating personalization preference:", error);
      // Revert the checkbox state on error
      setOptIn(!isOptedIn);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.landingContainer}>
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroImageContainer}>
          <img
            src="https://cache.marriott.com/is/image/marriotts7prod/bonvoy-points-redemption--33160?wid=1440"
            alt="Poolside breakfast with beverages"
            className={styles.heroImage}
          />
        </div>
      </div>

      {/* Content Section */}
      <div className={styles.contentSection}>
        <h1 className={styles.mainHeading}>
          Thank you! Your preferences have been saved.
        </h1>

        <p className={styles.subheading}>
          You can update these anytime in your Marriott Bonvoy profile.
        </p>

        <BtnUi
          data-testid="beverage-done"
          className={`${styles.getStartedButton} custom_click_track`}
          data-custom_click_track_value="Beverage Thank You|Done|internal"
          onClick={handleDone}
        >
          Done
        </BtnUi>

        <div className={styles.thankYouFooter}>
          <p className={styles.thankYouMessage}>
            By sharing your favorites, you&apos;re helping us update your Bonvoy
            profile so we can tailor your stay just for you.
          </p>

          <label className={styles.optOutLabel}>
            <input
              data-testid="beverage-personalized-recommendations"
              type="checkbox"
              className={`${styles.optOutCheckbox} custom_click_track`}
              data-custom_click_track_value="Beverage Thank You|'I want personalized recommendations' checkbox|internal"
              checked={optIn}
              onChange={handleOptInChange}
              disabled={isSaving}
            />
            <span className={styles.optOutText}>
              {isSaving
                ? "Updating..."
                : "I want personalized recommendations based on my preferences"}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};
